import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const TechnologyRadar = () => {
  const [activeQuadrant, setActiveQuadrant] = useState(null);

  const radarData = [
    {
      quadrant: 'Adopt',
      color: 'bg-emerald-500',
      description: 'Core stack used confidently in production',
      technologies: [
        { name: 'Java', position: { x: 14, y: 18 }, maturity: 'stable' },
        { name: 'Python', position: { x: 22, y: 14 }, maturity: 'stable' },
        { name: 'JavaScript', position: { x: 18, y: 24 }, maturity: 'stable' },
        { name: 'React', position: { x: 28, y: 18 }, maturity: 'stable' },
        { name: 'Node.js', position: { x: 24, y: 26 }, maturity: 'stable' },
        { name: 'PostgreSQL', position: { x: 30, y: 22 }, maturity: 'stable' },
        { name: 'MySQL', position: { x: 20, y: 30 }, maturity: 'stable' },
        { name: 'MongoDB', position: { x: 12, y: 26 }, maturity: 'stable' },
        { name: 'GitHub Actions', position: { x: 26, y: 12 }, maturity: 'stable' },
        { name: 'Docker', position: { x: 16, y: 12 }, maturity: 'stable' },
        { name: 'VS Code', position: { x: 14, y: 28 }, maturity: 'stable' }
      ]
    },
    {
      quadrant: 'Trial',
      color: 'bg-blue-500',
      description: 'Actively evaluating with small projects',
      technologies: [
        { name: 'TypeScript', position: { x: 64, y: 18 }, maturity: 'emerging' },
        { name: 'Spring', position: { x: 72, y: 14 }, maturity: 'emerging' },
        { name: '.NET', position: { x: 76, y: 24 }, maturity: 'emerging' },
        { name: 'SQLite', position: { x: 60, y: 28 }, maturity: 'emerging' },
        { name: 'Jupyter', position: { x: 68, y: 26 }, maturity: 'emerging' },
        { name: 'Pandas', position: { x: 70, y: 20 }, maturity: 'emerging' },
        { name: 'NumPy', position: { x: 62, y: 22 }, maturity: 'emerging' }
      ]
    },
    {
      quadrant: 'Assess',
      color: 'bg-amber-500',
      description: 'Exploring for future fit and depth',
      technologies: [
        { name: 'C', position: { x: 64, y: 72 }, maturity: 'experimental' },
        { name: 'C++', position: { x: 70, y: 78 }, maturity: 'experimental' },
        { name: 'C#', position: { x: 76, y: 70 }, maturity: 'experimental' },
        { name: 'Cisco Packet Tracer', position: { x: 62, y: 84 }, maturity: 'experimental' },
        { name: 'Kali Linux', position: { x: 74, y: 82 }, maturity: 'experimental' }
      ]
    },
    {
      quadrant: 'Hold',
      color: 'bg-slate-500',
      description: 'Not a focus right now / legacy',
      technologies: [
      ]
    }
  ];

  const rings = [
    { name: 'Languages & Frameworks', radius: 25 },
    { name: 'Tools & Platforms', radius: 50 },
    { name: 'Techniques & Methods', radius: 75 },
    { name: 'Infrastructure', radius: 100 }
  ];

  return (
    <div className="bg-card border-2 border-border rounded-xl p-8 lg:p-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-foreground">Technology Radar</h3>
          <p className="text-muted-foreground">Current technology adoption strategy</p>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Radar" size={20} color="var(--color-primary)" />
          <span className="text-sm text-muted-foreground">Updated: Oct 2025</span>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
        <div className="relative">
          <div className="w-full max-w-[340px] h-[340px] md:max-w-[400px] md:h-[400px] lg:max-w-[420px] lg:h-[420px] mx-auto relative">
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 200">
              {rings?.map((ring, index) => (
                <circle key={index} cx="100" cy="100" r={ring?.radius / 2} fill="none" stroke="var(--color-border)" strokeWidth="1" opacity="0.3" />
              ))}
              <line x1="100" y1="0" x2="100" y2="200" stroke="var(--color-border)" strokeWidth="1" opacity="0.3" />
              <line x1="0" y1="100" x2="200" y2="100" stroke="var(--color-border)" strokeWidth="1" opacity="0.3" />
            </svg>

            {radarData?.map((quadrant, qIndex) => (
              <div key={qIndex}>
                {quadrant?.technologies?.map((tech, tIndex) => (
                  <div key={tIndex} className={`absolute w-4 h-4 md:w-5 md:h-5 ${quadrant?.color} rounded-full cursor-pointer hover:scale-150 transition-transform group`} style={{ left: `${tech?.position?.x}%`, top: `${tech?.position?.y}%`, transform: 'translate(-50%, -50%)' }} onMouseEnter={() => setActiveQuadrant(qIndex)} onMouseLeave={() => setActiveQuadrant(null)}>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-popover border border-border rounded text-xs text-popover-foreground opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">{tech?.name}</div>
                  </div>
                ))}
              </div>
            ))}

            <div className="absolute top-2 left-2 text-xs font-medium text-muted-foreground">ADOPT</div>
            <div className="absolute top-2 right-2 text-xs font-medium text-muted-foreground">TRIAL</div>
            <div className="absolute bottom-2 right-2 text-xs font-medium text-muted-foreground">ASSESS</div>
            <div className="absolute bottom-2 left-2 text-xs font-medium text-muted-foreground">HOLD</div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <h4 className="font-semibold text-foreground mb-3">Quadrants</h4>
            <div className="space-y-3">
              {radarData?.map((quadrant, index) => (
                <div key={index} className={`p-3 rounded-lg border transition-all cursor-pointer ${activeQuadrant === index ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground'}`} onMouseEnter={() => setActiveQuadrant(index)} onMouseLeave={() => setActiveQuadrant(null)}>
                  <div className="flex items-center space-x-3 mb-2">
                    <div className={`w-3 h-3 ${quadrant?.color} rounded-full`} />
                    <span className="font-medium text-foreground">{quadrant?.quadrant}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{quadrant?.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {quadrant?.technologies?.map((tech, techIndex) => (
                      <span key={techIndex} className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded font-mono">{tech?.name}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-3">Rings</h4>
            <div className="space-y-2">
              {rings?.map((ring, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className="w-2 h-2 border border-muted-foreground rounded-full" />
                  <span className="text-sm text-muted-foreground">{ring?.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnologyRadar;
